// utils/notificationDeepLink.js
//
// Telegram notification deep links — TMA-only. Every automatic notification's
// Telegram message carries an "فتح" (Open) button pointing at
// `t.me/<bot>/<app>?startapp=go_<base64url(route)>`. This module turns that
// `start_param` back into the in-app route the member should land on.
//
// Why the route travels encoded: Telegram's `startapp` slot accepts only
// `[A-Za-z0-9_-]` (512 max), so a path with slashes cannot be sent literally.
// The backend encodes the WHOLE route rather than minting a per-notification
// verb, which means this one decoder covers all 29 notification types and a new
// backend notification type never needs a matching PWA release.
//
// Pure module — no Vue/F7 imports. See the backend brief
// `_bmad-output/sync/briefs/2026-08-14-telegram-cta-miniapp-deeplink.md`.

/** Marks a `start_param` as an encoded route. Disjoint from the two verbs
 *  already sharing this slot: the exact-match `launch`, and referral tokens
 *  (matched as `^ref[_-]` — see utils/referral.js). */
export const DEEP_LINK_PREFIX = 'go_';

/** Telegram's own `startapp` ceiling. A longer value can only be hand-crafted;
 *  no real notification produces one. */
export const DEEP_LINK_MAX_LENGTH = 512;

/**
 * Decode a base64url payload (no padding) back to its UTF-8 string.
 *
 * @param {string} payload
 * @returns {string|null} Decoded text, or null when the payload is not valid
 *   base64url / not valid UTF-8.
 */
function decodeBase64Url(payload) {
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = atob(padded);
    // atob yields one char per BYTE. Routes are ASCII today, but decoding the
    // bytes as UTF-8 keeps a non-ASCII route from silently arriving as mojibake.
    return decodeURIComponent(
      Array.from(binary, (ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
  } catch {
    // atob throws on invalid base64; decodeURIComponent throws on invalid UTF-8.
    return null;
  }
}

/**
 * True when the value contains a space, a C0/C1 control character, or DEL.
 *
 * Written as a code scan rather than a regex on purpose: the equivalent pattern
 * needs literal control characters in the source, which are invisible in review
 * and trip `no-control-regex`.
 *
 * @param {string} value
 * @returns {boolean}
 */
function hasUnsafeChars(value) {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f || (code >= 0x80 && code <= 0x9f)) return true;
  }
  return false;
}

/**
 * Decide whether a decoded value is a safe in-app destination.
 *
 * This is a SECURITY guard, not a formatting check: `start_param` is attacker-
 * supplied (anyone can craft a `t.me/<bot>/<app>?startapp=…` link and send it to
 * a member), and the value is about to be handed to the router. The checks are
 * structural rather than a character allowlist so that legitimate routes with a
 * query string keep working:
 *
 *  - must be same-origin-relative → a single leading `/`
 *  - `//evil.com` is protocol-relative and would leave the app → rejected
 *  - `\` is treated as `/` by some URL parsers → rejected outright
 *  - whitespace/control characters can smuggle past naive checks → rejected
 *
 * An unknown-but-safe path is deliberately allowed through: Framework7 simply
 * fails to match it and falls back to the default route.
 *
 * @param {string} route
 * @returns {boolean}
 */
function isSafeInAppRoute(route) {
  if (!route.startsWith('/')) return false;
  if (route.startsWith('//')) return false;
  if (route.includes('\\')) return false;
  return !hasUnsafeChars(route);
}

/**
 * Extract the in-app route from a raw TMA `start_param`.
 *
 * @param {unknown} startParam - Raw `tgWebAppStartParam` (may be undefined,
 *   empty, the `launch` verb, a `ref_…` token, or anything else).
 * @returns {string|null} A safe in-app path (e.g. `/issues/123/`), or null when
 *   the param is not a deep link or does not decode to a safe route.
 */
export function extractDeepLinkRoute(startParam) {
  if (typeof startParam !== 'string') return null;

  const trimmed = startParam.trim();
  if (!trimmed.startsWith(DEEP_LINK_PREFIX)) return null;
  if (trimmed.length > DEEP_LINK_MAX_LENGTH) return null;

  const payload = trimmed.slice(DEEP_LINK_PREFIX.length);
  if (!payload) return null;

  const route = decodeBase64Url(payload);
  if (route === null || route === '') return null;

  return isSafeInAppRoute(route) ? route : null;
}
